import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addMinutes, parse } from 'date-fns';
import { 
  Calendar, 
  Clock, 
  Users, 
  User,
  Plus,
  Loader2,
  Settings,
  BarChart3,
  DollarSign,
  TrendingUp,
  Eye,
  EyeOff,
  Bell,
  UserX,
  Palmtree,
  Zap,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Trash2,
  Building2,
  PhoneForwarded
} from 'lucide-react';
import { AccountDeletionButton } from '../../components/AccountDeletionButton';
import { DataExportButton } from '../../components/DataExportButton';
import RealTimeStats from '../../components/RealTimeStats';
import MedicalScribe from '../../components/MedicalScribe';
import CallTransferConfig from '../../components/CallTransferConfig';
import MedicalRepAndAnnouncements from '../../components/MedicalRepAndAnnouncements';
import DeviceTestModal from '../../components/DeviceTestModal';
import ProfessionalOnboarding from '../../components/ProfessionalOnboarding';
import PushNotificationToggle from '../../components/PushNotificationToggle';
import { Brain, Mic, Briefcase, Megaphone, Video } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProfessionalDashboard = () => {
  const { t, isRTL } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAvailability, setShowAvailability] = useState(false);
  const [slots, setSlots] = useState([]);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const [showRevenueStats, setShowRevenueStats] = useState(true);
  const [revenueStats, setRevenueStats] = useState(null);
  
  // Vacation mode
  const [showVacation, setShowVacation] = useState(false);
  const [vacationStart, setVacationStart] = useState('');
  const [vacationEnd, setVacationEnd] = useState('');
  const [vacations, setVacations] = useState([]);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // AI Medical Scribe toggle
  const [scribeEnabled, setScribeEnabled] = useState(false);
  const [showScribe, setShowScribe] = useState(false);
  
  // Call Transfer Config toggle
  const [showCallTransferConfig, setShowCallTransferConfig] = useState(false);
  
  // Medical Rep & Announcements toggle
  const [showMedicalRepConfig, setShowMedicalRepConfig] = useState(false);

  // Cabinet Management toggle
  const [cabinetManagementEnabled, setCabinetManagementEnabled] = useState(false);
  
  // Device Test Modal
  const [showDeviceTest, setShowDeviceTest] = useState(false);

  const [availabilityConfig, setAvailabilityConfig] = useState({
    start_time: '08:00',
    end_time: '18:00',
    slot_duration: 30,
    break_start: '12:00',
    break_end: '14:00'
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, waitlistRes, statsRes, vacationsRes, notifsRes, revenueRes, cabinetStatusRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`, { headers }),
        axios.get(`${API_URL}/api/waitlist`, { headers }),
        axios.get(`${API_URL}/api/professional/dashboard-stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/professional/vacations`, { headers }).catch(() => ({ data: { vacations: [] } })),
        axios.get(`${API_URL}/api/notifications?limit=5`, { headers }).catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        axios.get(`${API_URL}/api/professionals/stats/revenue`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/professional/cabinet-management-status`, { headers }).catch(() => ({ data: { cabinet_management_enabled: false } }))
      ]);
      setAppointments(appointmentsRes.data);
      setWaitlist(waitlistRes.data);
      if (statsRes.data) {
        setDashboardStats(statsRes.data);
      }
      if (revenueRes.data) {
        setRevenueStats(revenueRes.data);
      }
      setVacations(vacationsRes.data?.vacations || []);
      setNotifications(notifsRes.data?.notifications || []);
      setUnreadCount(notifsRes.data?.unread_count || 0);
      setCabinetManagementEnabled(cabinetStatusRes.data?.cabinet_management_enabled || false);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_URL}/api/availability/${user.id}?date=${dateStr}`,
        { headers }
      );
      if (response.data[0]?.slots) {
        setSlots(response.data[0].slots);
      } else {
        generateDefaultSlots();
      }
    } catch (error) {
      generateDefaultSlots();
    }
  };

  const generateDefaultSlots = () => {
    const { start_time, end_time, slot_duration, break_start, break_end } = availabilityConfig;
    const generatedSlots = [];
    
    let currentTime = parse(start_time, 'HH:mm', new Date());
    const endTime = parse(end_time, 'HH:mm', new Date());
    const breakStartTime = parse(break_start, 'HH:mm', new Date());
    const breakEndTime = parse(break_end, 'HH:mm', new Date());

    while (currentTime < endTime) {
      const slotEndTime = addMinutes(currentTime, slot_duration);
      const isBreak = currentTime >= breakStartTime && currentTime < breakEndTime;
      
      if (!isBreak) {
        generatedSlots.push({
          start_time: format(currentTime, 'HH:mm'),
          end_time: format(slotEndTime, 'HH:mm'),
          is_available: true
        });
      }
      
      currentTime = slotEndTime;
    }
    
    setSlots(generatedSlots);
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    fetchAvailability(selectedDate);
  }, [selectedDate]);

  const toggleCabinetManagement = async () => {
    try {
      const response = await axios.patch(`${API_URL}/api/professional/toggle-cabinet-management`, {}, { headers });
      setCabinetManagementEnabled(response.data.cabinet_management_enabled);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  // Release slot and notify patients
  const handleReleaseSlot = async (appointmentId, appointmentDatetime) => {
    try {
      await axios.post(`${API_URL}/api/slots/release`, {
        professional_id: user.id,
        datetime: appointmentDatetime,
        reason: "cancellation"
      }, { headers });
      toast.success('🔔 Créneau libéré ! Les patients en attente sont notifiés.');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la libération du créneau');
    }
  };

  // Auto-fill from waitlist
  const handleAutoFill = async (datetime) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/slots/auto-fill/${user.id}?datetime_str=${datetime}`,
        {},
        { headers }
      );
      if (response.data.auto_filled) {
        toast.success(`✅ Patient ${response.data.patient?.patient_name} proposé automatiquement !`);
        fetchData();
      } else {
        toast.info('Aucun patient en liste d\'attente');
      }
    } catch (error) {
      toast.error('Erreur lors du remplissage automatique');
    }
  };

  // Mark as no-show
  const handleMarkNoShow = async (appointmentId) => {
    try {
      await axios.post(`${API_URL}/api/appointments/${appointmentId}/mark-noshow`, {}, { headers });
      toast.success('Patient marqué comme absent');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Enable vacation mode
  const handleEnableVacation = async () => {
    if (!vacationStart || !vacationEnd) {
      toast.error('Veuillez sélectionner les dates');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/professional/vacation`, {
        start_date: vacationStart,
        end_date: vacationEnd,
        notify_patients: true
      }, { headers });
      toast.success(`Mode vacances activé. ${response.data.affected_appointments} RDV annulés et patients notifiés.`);
      setShowVacation(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Cancel vacation
  const handleCancelVacation = async (vacationId) => {
    try {
      await axios.delete(`${API_URL}/api/professional/vacation/${vacationId}`, { headers });
      toast.success('Vacances annulées');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const toggleSlotAvailability = (index) => {
    const newSlots = [...slots];
    newSlots[index].is_available = !newSlots[index].is_available;
    setSlots(newSlots);
  };

  const saveAvailability = async () => {
    try {
      await axios.post(
        `${API_URL}/api/availability`,
        {
          professional_id: user.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          slots: slots
        },
        { headers }
      );
      toast.success(t('success'));
      setShowAvailability(false);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    try {
      await axios.patch(`${API_URL}/api/appointments/${appointmentId}/cancel`, {}, { headers });
      toast.success(t('success'));
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleToggleRevenueStats = async () => {
    try {
      const response = await axios.patch(`${API_URL}/api/professionals/toggle-revenue-stats`, {}, { headers });
      setShowRevenueStats(response.data.show_revenue_stats);
      toast.success(t('success'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: 'default',
      confirmed: 'secondary',
      cancelled: 'destructive',
      completed: 'outline'
    };
    return <Badge variant={variants[status] || 'default'}>{t(`status_${status}`)}</Badge>;
  };

  const todayAppointments = appointments.filter(
    a => a.date === format(selectedDate, 'yyyy-MM-dd') && a.status !== 'cancelled'
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="professional-dashboard">
      {/* Onboarding Guide for New Professionals */}
      <ProfessionalOnboarding userId={user?.id} />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            {t('dashboard_welcome')}, Dr. {user?.last_name}!
          </h1>
          <p className="text-slate-500 mt-1">{user?.profile?.specialty}</p>
          
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <PushNotificationToggle token={token} />
            {user?.trial_ends_at && (
              <span className="trial-badge">
                {t('pricing_trial')} - Expire le {new Date(user.trial_ends_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">RDV aujourd'hui</p>
                  <p className="text-3xl font-bold">{dashboardStats?.today?.appointments || todayAppointments.length}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Revenus semaine</p>
                  <p className="text-3xl font-bold">{dashboardStats?.week?.revenue || 0}€</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-200" />
              </div>
              <p className="text-green-200 text-xs mt-1">Taux remplissage: {dashboardStats?.week?.fill_rate || 0}%</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Liste d'attente</p>
                  <p className="text-3xl font-bold">{dashboardStats?.waitlist?.count || waitlist.length}</p>
                </div>
                <Users className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${dashboardStats?.noshow_rate > 10 ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'} text-white`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-100 text-sm">Taux absence</p>
                  <p className="text-3xl font-bold">{dashboardStats?.noshow_rate || 0}%</p>
                </div>
                <UserX className="w-8 h-8 text-slate-200" />
              </div>
              <p className="text-slate-200 text-xs mt-1">{dashboardStats?.week?.noshows || 0} cette semaine</p>
            </CardContent>
          </Card>
        </div>

        {/* Real-Time Stats */}
        <div className="mb-8">
          <RealTimeStats userRole="professional" />
        </div>

        {/* Cabinet Management Toggle Card */}
        <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Module Gestion Cabinet</h3>
                  <p className="text-sm text-slate-500">
                    {cabinetManagementEnabled 
                      ? 'Synchronisez vos données avec votre outil de gestion existant'
                      : 'Activez pour gérer votre cabinet et synchroniser vos données'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {cabinetManagementEnabled && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="gap-2 bg-blue-500 text-white hover:bg-blue-600 border-0"
                    onClick={() => navigate('/cabinet')}
                  >
                    <Settings className="w-4 h-4" />
                    Accéder
                  </Button>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{cabinetManagementEnabled ? 'ON' : 'OFF'}</span>
                  <Switch
                    checked={cabinetManagementEnabled}
                    onCheckedChange={toggleCabinetManagement}
                    data-testid="cabinet-management-toggle"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button 
            variant="outline" 
            className="gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600"
            onClick={() => navigate('/professional/calendar-sync')}
            data-testid="btn-calendar-sync"
          >
            <RefreshCw className="w-4 h-4" />
            Synchronisation Calendrier
          </Button>
          
          <Button 
            variant="outline" 
            className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 hover:from-emerald-600 hover:to-teal-600"
            onClick={() => navigate('/professional/locations')}
            data-testid="btn-multi-locations"
          >
            <Building2 className="w-4 h-4" />
            Mes Établissements
          </Button>
          
          <Button 
            variant="outline" 
            className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 hover:from-amber-600 hover:to-orange-600"
            onClick={() => navigate('/professional/contract')}
            data-testid="btn-contract"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Mon Contrat
          </Button>
          
          {/* Call Transfer Config Button */}
          <Button 
            variant="outline" 
            className={`gap-2 ${showCallTransferConfig 
              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0' 
              : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 text-indigo-700 hover:from-indigo-100 hover:to-purple-100'
            }`}
            onClick={() => setShowCallTransferConfig(!showCallTransferConfig)}
            data-testid="btn-call-transfer-config"
          >
            <PhoneForwarded className="w-4 h-4" />
            Transferts d'Appel
          </Button>
          
          {/* AI Medical Scribe Toggle */}
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg border bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <Brain className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">Scribe IA</span>
            <Switch
              checked={scribeEnabled}
              onCheckedChange={(checked) => {
                setScribeEnabled(checked);
                setShowScribe(checked);
                toast.success(checked ? 'Scribe IA activé' : 'Scribe IA désactivé');
              }}
              data-testid="scribe-toggle"
            />
          </div>
          
          {/* Medical Rep & Announcements Button */}
          <Button 
            variant="outline" 
            className={`gap-2 ${showMedicalRepConfig 
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0' 
              : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700 hover:from-amber-100 hover:to-orange-100'
            }`}
            onClick={() => setShowMedicalRepConfig(!showMedicalRepConfig)}
            data-testid="btn-medical-rep-config"
          >
            <Briefcase className="w-4 h-4" />
            Représentants & Annonces
          </Button>
          
          <Dialog open={showVacation} onOpenChange={setShowVacation}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Palmtree className="w-4 h-4" />
                Mode Vacances
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Activer le mode vacances</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Date de début</Label>
                  <Input type="date" value={vacationStart} onChange={(e) => setVacationStart(e.target.value)} />
                </div>
                <div>
                  <Label>Date de fin</Label>
                  <Input type="date" value={vacationEnd} onChange={(e) => setVacationEnd(e.target.value)} />
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Tous les RDV pendant cette période seront annulés et les patients notifiés automatiquement.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowVacation(false)}>Annuler</Button>
                <Button onClick={handleEnableVacation} className="bg-amber-600 hover:bg-amber-700">
                  <Palmtree className="w-4 h-4 mr-2" />
                  Activer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" className="gap-2" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
          
          <Button 
            variant="outline" 
            className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={() => setShowDeviceTest(true)}
            data-testid="test-devices-btn-pro"
          >
            <Video className="w-4 h-4" />
            {t('teleconsultation.test_devices')}
          </Button>
          
          <DataExportButton />
          
          <AccountDeletionButton />
          
          {unreadCount > 0 && (
            <Badge className="bg-red-500 text-white">
              <Bell className="w-4 h-4 mr-1" />
              {unreadCount} notifications
            </Badge>
          )}
        </div>

        {/* Active Vacations */}
        {vacations.filter(v => v.status === 'active').length > 0 && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Palmtree className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-semibold text-amber-800">Mode Vacances Actif</p>
                    <p className="text-amber-600 text-sm">
                      Du {vacations[0]?.start_date} au {vacations[0]?.end_date}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleCancelVacation(vacations[0]?.id)}>
                  Désactiver
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Medical Scribe Panel */}
        {showScribe && (
          <Card className="mb-6 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Brain className="w-5 h-5 text-purple-600" />
                  Scribe Médical IA
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowScribe(false)}
                  className="text-purple-600 hover:text-purple-800"
                >
                  Réduire
                </Button>
              </div>
              <CardDescription>
                Enregistrez vos consultations et générez automatiquement des notes médicales structurées
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MedicalScribe />
            </CardContent>
          </Card>
        )}

        {/* Call Transfer Config Panel */}
        {showCallTransferConfig && (
          <div className="mb-6">
            <CallTransferConfig />
          </div>
        )}

        {/* Medical Rep & Announcements Panel */}
        {showMedicalRepConfig && (
          <div className="mb-6">
            <MedicalRepAndAnnouncements />
          </div>
        )}

        {/* Upcoming Appointments with Actions */}
        {dashboardStats?.upcoming_appointments?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Prochains RDV - Actions rapides
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardStats.upcoming_appointments.map((appt) => (
                  <div key={appt.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{appt.patient_name || 'Patient'}</p>
                        <p className="text-sm text-slate-500">{new Date(appt.datetime).toLocaleString('fr-FR')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReleaseSlot(appt.id, appt.datetime)}>
                        <XCircle className="w-4 h-4 mr-1" />
                        Libérer
                      </Button>
                      <Button size="sm" variant="outline" className="text-amber-600" onClick={() => handleMarkNoShow(appt.id)}>
                        <UserX className="w-4 h-4 mr-1" />
                        Absent
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats from old code */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">RDV aujourd'hui</p>
                  <p className="text-2xl font-bold text-slate-900">{todayAppointments.length}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total RDV</p>
                  <p className="text-2xl font-bold text-slate-900">{appointments.length}</p>
                </div>
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">En attente</p>
                  <p className="text-2xl font-bold text-slate-900">{waitlist.length}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Statistics Module */}
        <Card className="mb-8" data-testid="revenue-stats-card">
          <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Statistiques de Revenus</CardTitle>
                <CardDescription>Suivi de vos revenus en temps réel</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">
                {showRevenueStats ? 'Visible' : 'Masqué'}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleToggleRevenueStats}
                data-testid="toggle-revenue-stats"
              >
                {showRevenueStats ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          {showRevenueStats && revenueStats && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600 font-medium">Revenus Totaux</p>
                  <p className="text-2xl font-bold text-green-900">{revenueStats.total_revenue?.toFixed(2) || '0.00'}€</p>
                  <p className="text-xs text-green-500 mt-1">{revenueStats.total_appointments || 0} consultations</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-medium">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-blue-900">{revenueStats.today_revenue?.toFixed(2) || '0.00'}€</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-600 font-medium">Projeté</p>
                  <p className="text-2xl font-bold text-purple-900">{revenueStats.projected_revenue?.toFixed(2) || '0.00'}€</p>
                  <p className="text-xs text-purple-500 mt-1">{revenueStats.scheduled_appointments || 0} RDV planifiés</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-600 font-medium">Prix Consultation</p>
                  <p className="text-2xl font-bold text-amber-900">{revenueStats.consultation_price || 50}€</p>
                </div>
              </div>
              
              {/* Monthly Breakdown */}
              {revenueStats.by_month && Object.keys(revenueStats.by_month).length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Revenus par mois
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(revenueStats.by_month).map(([month, amount]) => (
                      <div 
                        key={month}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <span className="font-medium text-slate-700">{month}</span>
                        <span className="text-green-600 font-bold">{amount.toFixed(2)}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
          {!showRevenueStats && (
            <CardContent>
              <div className="text-center py-8 text-slate-500">
                <EyeOff className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Statistiques de revenus masquées</p>
                <p className="text-sm">Cliquez sur le bouton ci-dessus pour les afficher</p>
              </div>
            </CardContent>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar & Availability */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Calendrier
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateChange}
                  className="rounded-md border w-full"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  {t('professional_availability')}
                </CardTitle>
                <Dialog open={showAvailability} onOpenChange={setShowAvailability}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="manage-availability-btn">
                      Gérer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        Disponibilités - {format(selectedDate, 'dd/MM/yyyy')}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <Label>Début</Label>
                          <Input
                            type="time"
                            value={availabilityConfig.start_time}
                            onChange={(e) => setAvailabilityConfig({ ...availabilityConfig, start_time: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Fin</Label>
                          <Input
                            type="time"
                            value={availabilityConfig.end_time}
                            onChange={(e) => setAvailabilityConfig({ ...availabilityConfig, end_time: e.target.value })}
                          />
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full mb-4"
                        onClick={generateDefaultSlots}
                      >
                        Générer créneaux
                      </Button>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {slots.map((slot, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-center justify-between p-2 rounded-lg ${
                              slot.is_available ? 'bg-green-50' : 'bg-slate-100'
                            }`}
                          >
                            <span className="text-sm font-medium">
                              {slot.start_time} - {slot.end_time}
                            </span>
                            <Switch
                              checked={slot.is_available}
                              onCheckedChange={() => toggleSlotAvailability(idx)}
                              data-testid={`slot-toggle-${idx}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAvailability(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={saveAvailability} data-testid="save-availability-btn">
                        {t('save')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2">
                  {slots.slice(0, 9).map((slot, idx) => (
                    <div
                      key={idx}
                      className={`p-2 text-center text-xs rounded ${
                        slot.is_available 
                          ? 'bg-green-50 text-green-700 border border-green-200' 
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {slot.start_time}
                    </div>
                  ))}
                  {slots.length > 9 && (
                    <div className="p-2 text-center text-xs text-slate-500">
                      +{slots.length - 9}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Appointments */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  RDV du {format(selectedDate, 'dd/MM/yyyy')}
                </CardTitle>
                <CardDescription>
                  {todayAppointments.length} rendez-vous
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {todayAppointments.map((apt) => (
                    <div 
                      key={apt.id}
                      className={`flex items-center justify-between p-4 bg-slate-50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                      data-testid={`appointment-${apt.id}`}
                    >
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            Patient #{apt.patient_id.substring(0, 8)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {apt.start_time} - {apt.end_time}
                          </p>
                          {apt.reason && (
                            <p className="text-xs text-slate-400">{apt.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {getStatusBadge(apt.status)}
                        {apt.status === 'scheduled' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleCancelAppointment(apt.id)}
                            data-testid={`cancel-apt-${apt.id}`}
                          >
                            {t('appointment_cancel')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {todayAppointments.length === 0 && (
                    <p className="text-center text-slate-500 py-8">
                      {t('no_appointments')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Waitlist */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  {t('dashboard_waitlist')}
                </CardTitle>
                <CardDescription>
                  {t('patients_waiting')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {waitlist.map((entry) => (
                    <div 
                      key={entry.id}
                      className={`flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}
                      data-testid={`waitlist-${entry.id}`}
                    >
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            Patient #{entry.patient_id.substring(0, 8)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {entry.preferred_date_start} - {entry.preferred_date_end}
                          </p>
                          <p className="text-xs text-slate-400">
                            {entry.preferred_time_start} - {entry.preferred_time_end}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline">
                        {entry.status === 'waiting' ? 'En attente' : entry.status}
                      </Badge>
                    </div>
                  ))}
                  {waitlist.length === 0 && (
                    <p className="text-center text-slate-500 py-8">
                      Aucun patient en attente
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Device Test Modal for Teleconsultation */}
      <DeviceTestModal 
        isOpen={showDeviceTest}
        onClose={() => setShowDeviceTest(false)}
        onSuccess={() => toast.success(t('teleconsultation.test_success'))}
      />
    </div>
  );
};

export default ProfessionalDashboard;
